"use client";

import { useEffect, useState } from "react";
import { Post } from "@/types";
import { likeDocService, unLikeDocService } from "@/services/post";
import { followUserService, unfollowUserService } from "@/services/user";
import useLogin from "@/hooks/useLogin";
import { useStatus } from "@/hooks/useStatus";
import { track } from "@/lib/log";
const PADDING_LEFT = 52;


interface PostCardProps {
  post: Post & { isNowLiked?: boolean, isLiked?: boolean };
  onPostClick: (post: Post & { isNowLiked?: boolean, isLiked?: boolean }) => void;
  showFollow?: boolean;
}

export default function PostCard({ post, onPostClick, showFollow = true }: PostCardProps) {
  const { liked, toggleLiked } = useStatus({ docid: post.docid, media_id: post.media_id });
  const [isNowLiked, setIsNowLiked] = useState(post.isNowLiked || false);
  const [likeCount, setLikeCount] = useState(post.up);
  const { checkLogin } = useLogin();
  function changeLikeCount(isLiked: boolean, isNowLiked: boolean, up: number) {
    setLikeCount(() => {
      let res = up
      if (isLiked && !isNowLiked) {
        res = up - 1;//点赞-1
      } else if (!isLiked && isNowLiked) {
        res = up + 1;//点赞+1
      } else {
        res = up;
      }
      return res;
    })
  }
  useEffect(() => {
    changeLikeCount(post.isLiked || false, isNowLiked, post.up || 0);
  }, [isNowLiked, post.isLiked, post.up]);
  const handleLike = checkLogin(() => {
    track("like_post", {
      action: isNowLiked ? "unlike" : "like",
      doc_id: post?.docid,
      type: "click",
      page: "list"
    })
    setIsNowLiked(e => !e);
    toggleLiked(post.docid);
    if (liked) {
      unLikeDocService({ docid: post.docid });
    } else {
      likeDocService({ docid: post.docid });
    }
  });
  const [isFollowed, setIsFollowed] = useState(post?.followed == 1);
  useEffect(() => {
    if (post) {
      setIsFollowed(post.followed == 1);
    }
  }, [post]);
  const handleFollow = checkLogin(() => {
    track("follow_user", {
      action: isFollowed ? "unfollow" : "follow",
      doc_id: post?.docid,
      media_id: post?.media_id,
      type: "click"
    })
    setIsFollowed(!isFollowed);
    if (isFollowed) {
      unfollowUserService(post.media_id);
    } else {
      followUserService(post.media_id);
    }
  });

  const handleCardClick = () => {
    track("post_card_click", {
      action: "click",
      doc_id: post?.docid,
      type: "click",
      page: "list"
    })
    onPostClick({
      ...post,
      isNowLiked,
      followed: isFollowed ? 1 : 0,
    });
  };

  // 同步 isNowLiked 状态
  useEffect(() => {
    if (post) {
      setIsNowLiked(post.isNowLiked || false);
    }
  }, [post]);

  return (

    <article
      className="bg-background overflow-hidden hover:!bg-gray-50 transition-all duration-200 cursor-pointer rounded-xl px-2 py-2 sm:px-4 sm:py-6 w-full max-w-[900px] min-w-[100px]"
      onClick={handleCardClick}
    >
      <div>
        {/* Author info */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <div
              // href={`/users/${post?.author?.username}`}
              // onClick={(e) => e.stopPropagation()}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img
                src={post?.author?.avatar}
                alt={post?.author?.name}
                width={40}
                height={40}
                className="rounded-full !max-h-[40px] !max-w-[40px] min-w-[40px] min-h-[40px]"
              />
              <div>
                <div className="flex items-center space-x-1">
                  <span className="font-medium text-gray-900">
                    {post?.author?.username}
                  </span>
                  {post?.author?.verified && (
                    <svg
                      className="w-4 h-4 text-blue-500"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
                {/* <div className="text-sm text-gray-500">
                    {post.date_str}
                  </div> */}
              </div>
            </div>
          </div>
          {showFollow && <button
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleFollow();
            }}
          >
            {isFollowed ? (
              <span className="ml-1 text-sm font-medium">Following</span>
            ) : (
              <>
                <span className="text-xl">+</span>
                <span className="ml-1 text-sm font-medium">Follow</span>
              </>
            )}
          </button>
          }
        </div>

        {/* Post content */}
        <a className={`pl-[${PADDING_LEFT}px] pb-3 block`} href={post.web_post_path} onClick={e => { e.preventDefault(); }} >
          <h2 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
            {post.title}
          </h2>
          <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
            {post.content}
          </p>
        </a>

        {/* Post image */}
        {(post?.images || []).length > 0 && (
          <div
            className={`overflow-x-auto flex flex-row flex-nowrap gap-2 scrollbar-hide pl-[${PADDING_LEFT}px]`}
          >
            {post.images.map((image, index) => (
              <img
                key={`${image}-${index}`}
                src={image}
                alt={post.title}
                width={200}
                height={200}
                className="object-cover rounded-lg h-[200px] w-auto max-w-[200px]"
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div
        className={`flex items-center justify-between pl-[${PADDING_LEFT}px] py-3 border-t border-gray-50`}
      >
        <div className="flex items-center space-x-6">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleLike();
            }}
            className={`flex items-center space-x-2 text-sm transition-colors ${isNowLiked ? "text-red-500" : "text-gray-500 hover:text-red-500"
              }`}
          >
            <svg
              className={`w-5 h-5 ${isNowLiked ? "fill-current" : "fill-none"}`}
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
              />
            </svg>
            <span>{likeCount}</span>
          </button>

          <button
            onClick={handleCardClick}
            className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <span>{post.comments}</span>
          </button>

          <button className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors hidden">
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
              />
            </svg>
            <span>Share</span>
          </button>
        </div>
        {/* line   */}
      </div>

    </article>
  );
}
